import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { ArticleExperiencePhoto } from "@/mk2/apps/wiki/containers/ArticleExperiencePhoto/ArticleExperiencePhoto";
import { articleExperiencePhotoFetchTrigger } from "@/mk2/apps/wiki/containers/ArticleExperiencePhoto/ArticleExperiencePhoto.actions";
import { articlesExperiencePhotosReducer } from "@/mk2/apps/wiki/containers/ArticleExperiencePhoto/ArticleExperiencePhoto.reducers";
import articlesExperiencePhotosSaga from "@/mk2/apps/wiki/containers/ArticleExperiencePhoto/ArticleExperiencePhoto.sagas";
import { geArticleExperiencePhotosState } from "@/mk2/apps/wiki/containers/ArticleExperiencePhoto/ArticleExperiencePhoto.selectors";
import { isArticleOfReviewsCategory } from "@/mk2/apps/wiki/helpers";
import { wikiArticleUrl } from "@/mk2/apps/wiki/urls";
import { AppName } from "@/mk2/containers/Application/Application";
import { getFirstParagraphsPlaintext } from "@/mk2/helpers/article_ast/utils";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, { HalfpageProps, PageRouteProps, RightContentSide } from "@/mk2/pages/Page";
import { AppState } from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    ExperienceEntity,
    ExperiencePhotoEntity,
    ExperienceSchema,
    MenuSelection,
    WikiArticleEntity,
    WikiArticleSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntity } from "@/mk2/selectors";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import { SEO_TITLE_WIKI_article } from "@/mk/autogenerated/translation";

export interface OwnProps {}

export interface StateProps {
    experienceId: number;
    photoId: number;
    errorMessage: string;
    loadingState: LoadingState;
    article: WikiArticleEntity;
    experience: ExperienceEntity;
    experiencePhoto: ExperiencePhotoEntity;
}

export interface DispatchProps {
    onLoad(articleSlug: string, categorySlug: string, experienceId: number, photoId: number);
}

export type RouteProps = PageRouteProps<{
    categorySlug: string;
    articleSlug: string;
    experienceId: string;
    photoId: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ArticleExperiencePhotoPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const { routeParams } = ownProps;
            const categorySlug = routeParams.categorySlug;
            const articleSlug = routeParams.articleSlug;
            const experienceId = parseInt(routeParams.experienceId, 10);
            const photoId = parseInt(routeParams.photoId, 10);

            const photosState = geArticleExperiencePhotosState(state, categorySlug, articleSlug);
            const article: WikiArticleEntity = getDenormalizedEntity<WikiArticleEntity>(
                state,
                WikiArticleSchema,
                photosState.articleId,
            );
            const experience = getDenormalizedEntity<ExperienceEntity>(
                state,
                ExperienceSchema,
                experienceId,
            );
            const experiencePhoto = experience
                ? experience.photos.find((p) => p.photofile.id === photoId)
                : null;

            const loadingState =
                photosState.loadingState != LoadingState.SUCCESS
                    ? photosState.loadingState
                    : experiencePhoto
                      ? LoadingState.SUCCESS
                      : LoadingState.FAILURE;
            const errorMessage =
                photosState.loadingState != LoadingState.SUCCESS
                    ? photosState.errorMessage
                    : experiencePhoto
                      ? null
                      : "NOT FOUND";

            return {
                article,
                experience,
                experiencePhoto,
                experienceId,
                photoId,
                loadingState,
                errorMessage,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: articleExperiencePhotoFetchTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        const { article } = props;
        if (article) {
            // custom napisany SEO title, alebo to iste ako nadpis clanku
            return (
                article.seoTitle || interpolate(SEO_TITLE_WIKI_article, { article: article.title })
            );
        } else {
            return null;
        }
    }

    public getMetaDescription(props: Props): string {
        const { article } = props;
        if (article) {
            // custom napisany SEO description, alebo to iste ako prvy odstavec clanku
            return article.seoDescription || getFirstParagraphsPlaintext(article.bodyAST);
        } else {
            // pouzi defaultny description v Page
            return null;
        }
    }

    public getMetaFaqRichSnippet(props: Props): string {
        const { article } = props;
        return article?.seoFaqRichSnippet;
    }

    public render(props: Props): false | JSX.Element {
        return <ArticleExperiencePhoto {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        return null;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        const {
            routeParams: { categorySlug, articleSlug },
        } = props;
        return url(wikiArticleUrl, { categorySlug, articleSlug });
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        const { article } = props;
        if (article && !article.disabledAds && !isArticleOfReviewsCategory(article.category.slug)) {
            return {
                zone: "Wiki - Top 02 - Desktop",
            };
        }
        return null;
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: app,
            activeItem: props.article ? props.article.slug : null,
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer(
            "containers.wiki.articlesExperiencePhotos",
            articlesExperiencePhotosReducer,
        );
        store.injectSaga("containers.wiki.articlesExperiencePhotos", articlesExperiencePhotosSaga);
    }
}
